/**
 * Admin MFA enrollment — on mount calls useAdminSession.enroll() (returns the one-time secret,
 * otpauth URI, and recovery codes), shows them with a clear "save these now" warning, then accepts
 * a confirmation code via confirmEnroll(). The secret/recovery codes are shown ONCE.
 */
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { AdminMfaEnrollResponse } from '@beam/api-types';
import { useAdminSession } from '../../src/auth/adminSession';
import { AdminApiError } from '../../src/api/adminClient';
import { Screen, Text, Field, Button, Card } from '../../src/components/ui';
import { copyText } from '../../src/components/admin/clipboard';
import { theme } from '../../src/theme';

export default function AdminEnrollScreen() {
  const enroll = useAdminSession((s) => s.enroll);
  const confirmEnroll = useAdminSession((s) => s.confirmEnroll);

  const [data, setData] = useState<AdminMfaEnrollResponse | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await enroll();
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          setEnrollError(err instanceof Error ? err.message : 'Failed to start enrollment.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enroll]);

  async function handleConfirm() {
    const trimmed = code.trim();
    if (!trimmed) {
      setConfirmError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setConfirmError(null);
    setLoading(true);
    try {
      await confirmEnroll(trimmed);
      // Gate routes to /admin once status → 'authenticated'.
    } catch (err) {
      if (err instanceof AdminApiError || err instanceof Error) {
        setConfirmError(err.message);
      } else {
        setConfirmError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Screen scroll maxWidth={520}>
      <View style={styles.brand}>
        <Text variant="title">Set up two-factor authentication</Text>
        <Text variant="subhead" color="secondary">
          Add this account to an authenticator app, then confirm with a generated code.
        </Text>
      </View>

      {enrollError ? (
        <Card>
          <Text color="danger">{enrollError}</Text>
        </Card>
      ) : !data ? (
        <Card>
          <Text color="secondary">Generating your enrollment secret…</Text>
        </Card>
      ) : (
        <>
          <Card>
            <Text variant="label" color="secondary">
              Secret
            </Text>
            <Text variant="data" selectable>
              {data.secret}
            </Text>
            <Button title="Copy secret" size="sm" variant="secondary" fullWidth={false} onPress={() => void copyText(data.secret)} />

            <Text variant="label" color="secondary">
              otpauth URI
            </Text>
            <Text variant="data" selectable>
              {data.otpauthUri}
            </Text>
            <Button title="Copy URI" size="sm" variant="secondary" fullWidth={false} onPress={() => void copyText(data.otpauthUri)} />
          </Card>

          <Card>
            <Text variant="bodyStrong" color="danger">
              Save these recovery codes now
            </Text>
            <Text variant="small" color="secondary">
              Each code works once if you lose your authenticator. They will not be shown again.
            </Text>
            <View style={styles.codes}>
              {data.recoveryCodes.map((rc) => (
                <Text key={rc} variant="data" selectable>
                  {rc}
                </Text>
              ))}
            </View>
            <Button
              title="Copy all recovery codes"
              size="sm"
              variant="secondary"
              fullWidth={false}
              onPress={() => void copyText(data.recoveryCodes.join('\n'))}
            />
          </Card>

          <Card>
            <Field
              label="Confirmation code"
              value={code}
              onChangeText={setCode}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="number-pad"
              textContentType="oneTimeCode"
              placeholder="123456"
              editable={!loading}
              accessibilityLabel="Confirmation code"
              returnKeyType="done"
              onSubmitEditing={handleConfirm}
              error={confirmError ?? undefined}
            />
            <Button title="Confirm and finish" onPress={handleConfirm} loading={loading} disabled={loading} size="lg" />
          </Card>
        </>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  brand: { gap: theme.spacing.sm },
  codes: { gap: theme.spacing.xs },
});
